/**
 * Hits from `POST /addresses/sanctions/batch`.
 *
 * The backend answers `{ total_checked, flagged_count, flagged: { [address]: hit }, clean_count }`,
 * keyed by the caller's own address string (case preserved). The Workbench used to expect a list
 * of `{ address, is_sanctioned }` rows, so `hits` was always empty and every batch screen said
 * "all clear": a false negative on sanctions. Both shapes are accepted here so an older backend
 * still works.
 */
export interface SanctionBatchHit {
    address: string;
    [key: string]: unknown;
}

export function sanctionHitsFromBatch(results: unknown): SanctionBatchHit[] {
    if (!results) return [];
    if (Array.isArray(results)) {
        return results
            .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object' && (r as Record<string, unknown>).is_sanctioned === true)
            .map((r) => ({ ...r, address: String(r.address ?? '') }))
            .filter((r) => r.address);
    }
    if (typeof results === 'object') {
        const flagged = (results as { flagged?: unknown }).flagged;
        if (flagged && typeof flagged === 'object' && !Array.isArray(flagged)) {
            return Object.entries(flagged as Record<string, unknown>)
                .filter(([address]) => !!address)
                .map(([address, hit]) => ({ ...(hit && typeof hit === 'object' ? (hit as Record<string, unknown>) : {}), address }));
        }
    }
    return [];
}

/** How many addresses the backend says it screened, when it says so. */
export function sanctionBatchTotal(results: unknown, fallback: number): number {
    const n = results && typeof results === 'object' && !Array.isArray(results)
        ? Number((results as { total_checked?: unknown }).total_checked)
        : NaN;
    return Number.isFinite(n) && n >= 0 ? n : fallback;
}
